/**
 * End-to-end (Cypress) test mode helpers.
 *
 * In E2E mode the app bypasses the Clerk sign-in gate so tests can drive the
 * real UI without authenticating against Clerk. Supabase access is unaffected
 * because the Supabase client uses the publishable (anon) key directly and does
 * not depend on a Clerk session (see src/lib/supabase.ts).
 *
 * Mode is enabled either by a build-time flag (`BUN_PUBLIC_E2E=true`) or, in
 * non-production builds only, by running inside Cypress (`window.Cypress` is
 * injected by the test runner). It is a no-op in normal production/dev usage.
 *
 * The runtime `window.Cypress` check is deliberately ignored in production
 * builds: it can be spoofed by any script in the page, so honoring it in
 * production would let a hostile client bypass the Clerk sign-in gate. Tests run
 * against a development build (`bun dev`), where auto-detecting Cypress without a
 * special build is worth the convenience; dedicated E2E builds can still opt in
 * explicitly via `BUN_PUBLIC_E2E`. `process.env.NODE_ENV` is baked in at build
 * time (see scripts/build-frontend.ts).
 */
export function isE2EMode(): boolean {
  // Explicit build-time opt-in always wins.
  //
  // The bundler substitutes `process.env.BUN_PUBLIC_E2E` with a string literal
  // ONLY when the variable is set at build time. When it is unset (normal dev
  // and production builds) the reference survives into the browser bundle,
  // where `process` does not exist — reading it directly throws
  // "process is not defined" and blanks the whole app. Reading it inside
  // try/catch keeps the literal substitution working for real E2E builds while
  // safely treating its absence as "not E2E" everywhere else.
  let e2eFlag: string | undefined;
  try {
    e2eFlag = process.env.BUN_PUBLIC_E2E;
  } catch {
    e2eFlag = undefined;
  }
  if (e2eFlag === "true") return true;

  // Never trust the spoofable runtime signal in production builds.
  // `process.env.NODE_ENV` is always baked in at build time across every build
  // path, so reading it directly is safe.
  if (process.env.NODE_ENV === "production") return false;
  return typeof window !== "undefined" && Boolean((window as unknown as { Cypress?: unknown }).Cypress);
}
